import React from "react";
import { Index } from "./IndexMass";
import { App as AppZaun } from "../App";
import { ArrangementMass } from "./ArrangementMass";
import "./style/Torkonfigurator.scss";
import { PreviewPictures } from "./PreviewPictures";
import ReactDOM from "react-dom";
import { Grundriss } from "../arrangement/Grundriss";
import { filterTests, slurpTests } from "oc-e2e-testrunner";
import { v3 } from "oc-threejs/short3";
import * as Commons from "oc-commons/Commons";
import { SnapshotRenderer } from "oc-threejs/SnapshotRenderer";
import { Urls } from "oc-commons/Urls";
import * as Dialogs from "oc-ui/dialogs/Dialogs";
import { UrlField } from "../components/zauneditor/FertigDialog";

export class AppMass extends AppZaun {
  /** @type {ArrangementMass} */
  arrangement;
  isTorkonfigurator = true;

  pageSequence = ["Masseditor"];
  constructor(container, configurationSettings) {
    super(container, configurationSettings);

    const code = Urls.urlParam("Code");
    this.on("loadedConfiguration", () => {
      if (code && code.length >= 8 && code.length <= 12 && !APP.configuration?.Code)
        this.showAlterTorkonfiguratorUmleitung(code);
    });
  }

  initArrangement() {
    this.arrangement = new ArrangementMass();
    this.previewPictures = new PreviewPictures();
    this.update3d();
    this.on("loadedConfiguration", () => {
      this.previewPictures.setOrthographicMode(false);
      this.initMass();
    });

    this.on("windowResizeEnd", APP.dirty);
  }

  /**
   * Initialisiert die Torkonfigurator-Extras im Arrangement und die Vorschaubilder inkl. Bemaßung
   */
  initMass() {
    this.arrangement.initMass();
    this.arrangement.konfiguration.handleUrlParams(this.urlParams);
    this.previewPictures.init();
  }

  createDefaultConfiguration() {
    const grundriss = new Grundriss();
    grundriss.setStandardForm("Strecke", [20]);
    return {
      Json: {
        grundriss: {
          ...grundriss.toJSON(),
        },
        inspiration: this.inspirationName,
        zaunart: this.zaunartName,
        pageName: "Masseditor",
        verteilungsstrategieAuswahlwert: "Individuell",
      },
    };
  }

  getStageAsConfiguration() {
    const configuration = super.getStageAsConfiguration();
    if (configuration) configuration.Masskonfigurator = true;
    return configuration;
  }

  parseParams() {
    super.parseParams();
    this.pageNameFromParam = "Masseditor";
    this.formName = "Strecke";
    this.laengeParameter = 6;
  }

  initUI() {
    ReactDOM.render(<Index />, this.container); // IndexMass
    this.ui = { dirty: () => this.updateUIDebounced() };
    APP.on("afterUpdate3d", () => this.configurationChangedDebounced?.());
    APP.on("undoHistoryChanged", () => APP.updateUIDebounced());
  }

  updateUI() {
    const t0 = performance.now();
    this.trigger("beforeUpdateUI");
    setTimeout(() => {
      ReactDOM.render(<Index />, this.container);
      const t1 = performance.now();
    }, 10);
    this.previewPictures.debouncedRender();
  }

  resolvePageName(name) {
    if (this.pages[name]) {
      return name;
    }
    return "Masseditor";
  }

  setCameraConfig(cameraConfig) {
    // Bei restore nichts tun, da die orthographische Kamera automatisch zentriert wird
    // und die Kamera-Wiederherstellung für orthographische Kameras grade eh nicht funktioniert
  }

  async render2dShot() {
    const width = 1200;
    const height = 800;
    this.previewPictures.setOrthographicMode(true);
    this.update3dPlain();
    this.update3dPlain();
    const renderer = new SnapshotRenderer();
    renderer.scene = this.scene;
    const clonedCamera = STAGE.camera.clone();
    // Die orthographische Kamera hat ein Frustum das auf die aktuelle Canvas-Größe ausgelegt ist.
    // Das Seitenverhältnis der Canvas weicht (besonders auf der Abschluss-Seite mit bigPreview) vom
    // Snapshot-Seitenverhältnis (1200:800) ab, was zu Verzerrungen führt.
    // top/bottom bleiben korrekt (abhängig vom Bounding-Sphere-Radius), nur left/right wird angepasst.
    if (clonedCamera.isOrthographicCamera) {
      const orthographicZoomFactor = APP.arrangement?.getCurrentElement()?.isSchiebetor ? 1.45 : 1;
      const halfHeight = clonedCamera.top / orthographicZoomFactor;
      const targetAspect = width / height;
      clonedCamera.top = halfHeight;
      clonedCamera.bottom = -halfHeight;
      clonedCamera.left = -halfHeight * targetAspect;
      clonedCamera.right = halfHeight * targetAspect;
      clonedCamera.updateProjectionMatrix();
    }
    renderer.camera = clonedCamera;
    renderer.setSize(width, height);

    const warningVisualizers = (APP.arrangement.warnungen ?? [])
      .map((warnung) => warnung?.jaco3d)
      .filter((warnung3d) => warnung3d != null)
      .map((warnung3d) => ({ warnung3d, visible: warnung3d.visible }));

    warningVisualizers.forEach(({ warnung3d }) => {
      warnung3d.visible = false;
    });
    let blob = null;
    try {
      blob = await renderer.getBlobPromise(renderer.scene);
    } finally {
      warningVisualizers.forEach(({ warnung3d, visible }) => {
        warnung3d.visible = visible;
      });
    }
    this.previewPictures.setOrthographicMode(false);
    return blob;
  }

  async getPdfContentBlobs() {
    const box = this.arrangement.getBox();
    const boxDistance = box.getSize(v3()).distanceTo(v3(0, 0, 0));
    // Für kleine Zaunanlagen müssen wir etwas weiter rauszoomen.
    const zoom = boxDistance < 6 ? 6 / boxDistance : 1;

    const oldOrthographicMode = STAGE.orthographicMode;
    this.previewPictures.resetView();
    await Commons.sleep(100);
    const gesamtansichtBlob = await this.render2dShot();
    await Commons.sleep(500);
    const schnappschussBlob = await this.renderZoomToBox(v3(0, 0.3, 1), box, zoom);
    this.previewPictures.setOrthographicMode(oldOrthographicMode);
    return [
      { fileName: "schnappschuss.jpg", blob: schnappschussBlob },
      { fileName: "gesamtansicht.jpg", blob: gesamtansichtBlob },
    ];
  }

  /**
   * getTestCases genau wie im Zaunplaner, aber hier sind wir im Unterverzeichnis torkonfigurator
   * daher werden hier nur die Testcases aus torkonfigurator/testsE2E geladen
   */
  getTestCases(testNameFilter) {
    const testCases = filterTests(
      slurpTests(require.context("./testsE2E", true, /.*\.(js|ts|jsx|tsx)$/)),
      testNameFilter
    );
    return testCases;
  }

  /**
   * Verändert die Höhe des OptionMenus um den Inhalt genau abzudecken,
   * nicht zu viel und nicht zu wenig.
   * Das ist notwendig, da das Menu aus einem langen div besteht, welches die Höhe der höchsten
   * Content-Seite annimmt. Wenn viel weniger im aktuellen Fenster angezeigt wird, so soll
   * der View schrumpfen, damit die Preview-Bilder näher rücken können
   *
   */
  updateMenuContainerHeight() {
    if (window.innerWidth > 1100) return;

    setTimeout(() => {
      const menuContentContainer = document.getElementById("optionMenuContent");
      const currentContentContainer = document.querySelector(".selectedContent > *");
      if (menuContentContainer) {
        // Beginn Extrahöhe für lange offene Infofelder
        let extraDiff = 0;
        if (currentContentContainer.getClientRects) {
          const openInfoFields = document.querySelectorAll(
            ".infoContent.open, .selectNumber__menu"
          );
          let lowestBottom = 0;
          for (let i = 0; i < openInfoFields.length; i++) {
            const elementToCheck = openInfoFields[i];
            const elementBottom = elementToCheck.getClientRects()[0].bottom;
            if (elementBottom > lowestBottom) lowestBottom = elementBottom;
          }
          if (currentContentContainer.getClientRects().length == 0) return;
          const menuBottom = currentContentContainer.getClientRects()[0].bottom;
          extraDiff = Math.max(lowestBottom - menuBottom + 15, 0);
          // Ende Extrahöhe für lange offene Infofelder
        }

        menuContentContainer.style.height =
          currentContentContainer.offsetHeight + extraDiff + 30 + "px";
      }
    }, 1);
  }

  showAlterTorkonfiguratorUmleitung(code) {
    const redirectUrl = APP.settings.alterTorkonfiguratorUrl + "?Code=" + code;
    Dialogs.showDialog({
      content: (
        <div className="umleitungDialog">
          <div
            className="flex-column flex-align-center"
            dangerouslySetInnerHTML={{ __html: I18N("Alter Torkonfigurator Umleitung") }}
          ></div>
          <br />
          <UrlField url={redirectUrl} />
        </div>
      ),
      buttonAndActionList: [
        {
          label: I18N("Link folgen"),
          primary: true,
          action: () => {
            window.location.href = redirectUrl;
          },
        },
        {
          label: I18N("Hier bleiben"),
          primary: true,
          action: () => {},
        },
      ],
    });
  }
}

/**
 * @param {HTMLElement} container
 * @param {Object} settings
 * @returns {AppMass}
 */
self.configurator = function (container, settings) {
  return new AppMass(container, settings);
};
